import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import type { DashboardBannerStrip, DashboardHeaderChrome, DashboardHeaderWidget } from './types';
import { TIME_WINDOW_OPTIONS, type TimeWindowOption } from './dashboardTimeWindowOptions';
import { HEADER_NUMBER_METRIC_OPTIONS } from './headerWidgetSchema';
import HeaderCategoryFilterControl from './HeaderCategoryFilterControl';
import { useWebMapContext } from '../webMap/WebMapContext';

function titleVariant(size: DashboardHeaderChrome['titleFontSize']): 'subtitle1' | 'h5' | 'h4' {
  if (size === 'small') return 'subtitle1';
  if (size === 'large') return 'h4';
  return 'h5';
}

function HeaderFilterShell(props: {
  editMode: boolean;
  onRemove?: () => void;
  onConfigure?: () => void;
  children: React.ReactNode;
}) {
  const { editMode, onRemove, onConfigure, children } = props;
  return (
    <Stack direction="row" alignItems="stretch" spacing={0.5} sx={{ position: 'relative' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      {editMode && (
        <Stack direction="row" spacing={0} sx={{ alignSelf: 'center', flexShrink: 0 }}>
          {onConfigure && (
            <Tooltip title="Widget settings (schema / metric)">
              <IconButton size="small" aria-label="Widget settings" onClick={onConfigure}>
                <SettingsIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
          {onRemove && (
            <Tooltip title="Remove filter">
              <IconButton size="small" aria-label="Remove filter" onClick={onRemove} color="warning">
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Stack>
  );
}

const DashboardHeaderBar: React.FC<{
  chrome: DashboardHeaderChrome;
  banner?: DashboardBannerStrip;
  widgets: DashboardHeaderWidget[];
  editMode: boolean;
  onAddWidget: (w: DashboardHeaderWidget) => void;
  onRemoveWidget: (id: string) => void;
  onConfigureWidget: (w: DashboardHeaderWidget) => void;
}> = ({
  chrome,
  banner,
  widgets,
  editMode,
  onAddWidget,
  onRemoveWidget,
  onConfigureWidget,
}) => {
  const webMapCtx = useWebMapContext();
  const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);
  const [lastUpdatedClock, setLastUpdatedClock] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setLastUpdatedClock(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const renderCategory = (w: Extract<DashboardHeaderWidget, { type: 'header_category' }>) => {
    return (
      <HeaderFilterShell
        editMode={editMode}
        onRemove={() => onRemoveWidget(w.id)}
        onConfigure={() => onConfigureWidget(w)}
      >
        <HeaderCategoryFilterControl widget={w} />
      </HeaderFilterShell>
    );
  };

  const renderDate = (w: Extract<DashboardHeaderWidget, { type: 'header_date' }>) => {
    const label = w.label?.trim() || 'Time window';
    const hint = w.hintText?.trim();
    const currentTw = webMapCtx?.timeWindow ?? 'All Time';
    return (
      <HeaderFilterShell
        editMode={editMode}
        onRemove={() => onRemoveWidget(w.id)}
        onConfigure={() => onConfigureWidget(w)}
      >
        <Stack spacing={0.25} sx={{ minWidth: 160, maxWidth: 220 }}>
          {hint ? (
            <Typography variant="caption" color="text.secondary" noWrap title={hint}>
              {hint}
            </Typography>
          ) : null}
          <FormControl size="small" sx={{ minWidth: 160, maxWidth: 200 }} fullWidth>
            <InputLabel id={`dh-date-${w.id}`}>{label}</InputLabel>
            <Select
              labelId={`dh-date-${w.id}`}
              label={label}
              value={currentTw}
              onChange={(e) =>
                webMapCtx?.setTimeWindow(e.target.value as TimeWindowOption)
              }
            >
              {TIME_WINDOW_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </HeaderFilterShell>
    );
  };

  const renderNumber = (w: Extract<DashboardHeaderWidget, { type: 'header_number' }>) => {
    const metricOpt = HEADER_NUMBER_METRIC_OPTIONS.find((m) => m.value === w.metric);
    const label =
      w.label?.trim() ||
      (w.mode === 'static' ? 'Value' : metricOpt?.label ?? w.metric?.replace(/_/g, ' ') ?? 'Metric');

    let body: string;
    if (w.mode === 'static') {
      body = w.staticValue?.trim() || '—';
    } else if (webMapCtx) {
      const firstLayerId = webMapCtx.layers[0]?.id;
      if (!firstLayerId) {
        body = '0';
      } else {
        const features = webMapCtx.getFeaturesForLayer(firstLayerId);
        if (w.metric === 'feature_count') {
          body = features.length.toLocaleString();
        } else if (w.metric === 'devices_filtered') {
          const unique = new Set(features.map((f) => f.properties?.entity_id ?? f.properties?.name ?? f.id));
          body = unique.size.toLocaleString();
        } else {
          body = features.length.toLocaleString();
        }
      }
    } else {
      body = '--';
    }

    return (
      <HeaderFilterShell
        editMode={editMode}
        onRemove={() => onRemoveWidget(w.id)}
        onConfigure={() => onConfigureWidget(w)}
      >
        <Stack spacing={0.25} sx={{ minWidth: 100, maxWidth: 200, py: 0.5, justifyContent: 'center' }}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={600} noWrap>
            {body}
          </Typography>
        </Stack>
      </HeaderFilterShell>
    );
  };

  const renderWidget = (w: DashboardHeaderWidget) => {
    switch (w.type) {
      case 'header_category':
        return <React.Fragment key={w.id}>{renderCategory(w)}</React.Fragment>;
      case 'header_date':
        return <React.Fragment key={w.id}>{renderDate(w)}</React.Fragment>;
      case 'header_number':
        return <React.Fragment key={w.id}>{renderNumber(w)}</React.Fragment>;
      default:
        return null;
    }
  };

  const titleSx = {
    color: chrome.titleColor ?? undefined,
    lineHeight: 1.2,
  };
  const subSx = {
    color: chrome.subtitleColor ?? 'text.secondary',
    mt: 0.25,
  };

  const barBg = chrome.backgroundColor ?? undefined;
  const logoUrl = chrome.logoUrl?.trim();

  const titleBlock = (
    <Stack spacing={0} sx={{ minWidth: 0, flex: '0 1 auto', mr: 2 }}>
      <Typography variant={titleVariant(chrome.titleFontSize)} component="div" sx={titleSx}>
        {chrome.title}
      </Typography>
      {chrome.subtitle?.trim() ? (
        <Typography variant="caption" sx={subSx}>
          {chrome.subtitle}
        </Typography>
      ) : null}
    </Stack>
  );

  const logoBlock = (side: 'left' | 'right') =>
    logoUrl ? (
      <Box
        component="img"
        src={logoUrl}
        alt=""
        sx={{
          height: 40,
          width: 'auto',
          maxWidth: 120,
          objectFit: 'contain',
          flexShrink: 0,
          ml: side === 'right' ? 1 : 0,
          mr: side === 'left' ? 1 : 0,
        }}
      />
    ) : null;

  const pushId = () => `hw_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  return (
    <>
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.25,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: barBg ?? 'action.hover',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          {chrome.logoAlign === 'right' ? null : logoBlock('left')}
          {titleBlock}
          {chrome.logoAlign === 'right' ? logoBlock('right') : null}

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ flex: 1, justifyContent: 'flex-end', minWidth: 0 }}
          >
            {widgets.map((w, i) => (
              <React.Fragment key={w.id}>
                {i > 0 ? <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} /> : null}
                {renderWidget(w)}
              </React.Fragment>
            ))}

            {editMode && (
              <>
                <Tooltip title="Add header widget (configure field in settings)">
                  <IconButton
                    color="primary"
                    size="small"
                    sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}
                    onClick={(e) => setAddAnchor(e.currentTarget)}
                    aria-label="Add header widget"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={addAnchor}
                  open={Boolean(addAnchor)}
                  onClose={() => setAddAnchor(null)}
                  MenuListProps={{ dense: true }}
                >
                  <MenuItem
                    onClick={() => {
                      onAddWidget({
                        id: pushId(),
                        type: 'header_date',
                        label: 'Time window',
                      });
                      setAddAnchor(null);
                    }}
                  >
                    Date selector
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      onAddWidget({
                        id: pushId(),
                        type: 'header_category',
                        schemaField: 'course',
                      });
                      setAddAnchor(null);
                    }}
                  >
                    Category selector
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      onAddWidget({
                        id: pushId(),
                        type: 'header_number',
                        mode: 'dynamic',
                        metric: 'devices_filtered',
                      });
                      setAddAnchor(null);
                    }}
                  >
                    Number
                  </MenuItem>
                </Menu>
              </>
            )}

            {chrome.showLastUpdated !== false ? (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Updated {lastUpdatedClock.toLocaleTimeString()}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </Box>

      {banner?.text?.trim() ? (
        <Box
          sx={{
            py: 0.75,
            px: 2,
            textAlign: 'center',
            bgcolor: banner.backgroundColor ?? 'secondary.dark',
            color: banner.textColor ?? 'common.white',
          }}
        >
          <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
            {banner.text.trim()}
          </Typography>
        </Box>
      ) : null}
    </>
  );
};

export default DashboardHeaderBar;
